import { i18n } from '../i18n.js';
import { MoonfallClickGuard } from '../utils/click-guard.js';
/**
 * Mulligan UI Module
 * Handles pre-game card selection phase where players can discard and redraw cards
 */


import { AssetPreloader } from './asset-preloader.js';
import { CardDescEngraving } from './card-desc-engraving.js';
import { CardHover } from './card-hover.js';
import { Cards } from './cards.js';
import { FX } from './fx.js';
import { MatchLoading } from './match-loading.js';
import { TouchPreview } from './touch-preview.js';
import { Tutorial } from './tutorial.js';
import { SocketHandler } from '../socket-handler.js';
let Mulligan = {};

Mulligan = (function () {
    const MULLIGAN_HOVER_LEAVE_MS = 300;
    // Hard cap on the raster/warm reveal gate (docs/asset-loading.md law 2).
    const MULLIGAN_REVEAL_CAP_MS = 1500;
    const MULLIGAN_PREVIEW_ID = 'mulligan-card-hover-preview';
    const MULLIGAN_INSPECTION_CLASS = 'mulligan-card-inspection-active';
    // The popup itself tops out at 3.5x. A tiny reserve above that absorbs the
    // browser's whole-pixel border quantization (which can enlarge the inner
    // art well by one CSS pixel at half-step scales) without ever stretching
    // the bitmap or spending another decode on the player's first inspection.
    const MULLIGAN_RASTER_RESERVE_SCALE = 3.55;

    function prefersReducedMotion() {
        return !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    }

    function removePreviewGuards(state) {
        if (!state) return;
        if (state.onPointerMove) {
            document.removeEventListener('pointermove', state.onPointerMove, true);
            state.onPointerMove = null;
        }
        if (state.onViewportChange) {
            window.removeEventListener('resize', state.onViewportChange);
            window.removeEventListener('orientationchange', state.onViewportChange);
            state.onViewportChange = null;
        }
    }

    function isPointerWithinPreview(state, wrapper, pointer) {
        if (!state || !pointer) return false;
        const containsPoint = (rect) => pointer.x >= rect.left
            && pointer.x <= rect.right
            && pointer.y >= rect.top
            && pointer.y <= rect.bottom;
        return containsPoint(wrapper.getBoundingClientRect())
            || containsPoint(state.cardElement.getBoundingClientRect());
    }

    function restoreMulliganPreview(wrapper, cardElement) {
        const state = wrapper._mulliganPreviewState;
        if (!state) return;
        wrapper._mulliganPreviewState = null;

        removePreviewGuards(state);
        if (cardElement.parentNode !== wrapper) wrapper.appendChild(cardElement);
        cardElement.style.cssText = state.cardStyle;
        cardElement.className = state.cardClassName;
        wrapper.style.width = state.wrapperWidth;
        wrapper.style.height = state.wrapperHeight;
        state.preview.remove();
        // Back in the row at rest: the natural paragraph, byte-identical.
        CardDescEngraving.release(cardElement);
        // A physically sized popup can consume the same backing pixels at 1x
        // that the row card declared as its enlargement reserve. Restore that
        // truthful reserve profile after returning it so the next inspection
        // remains sharp even after a viewport change.
        Cards?.restoreCardRasterReserve?.(cardElement);
    }

    function mountMulliganPreview(wrapper, cardElement, scale) {
        /* The inspected card IS the row card, re-laid-out at 3.5x — where a
           line on its wrap point re-wraps. Engrave its resting lines while it
           still sits in the row; applyPreviewScale then leaves the engraved
           size and lines alone (card-desc-engraving.js), and
           restoreMulliganPreview releases them. */
        if (Number.isFinite(scale) && scale > 1) CardDescEngraving.engrave(cardElement);
        const cardRect = cardElement.getBoundingClientRect();
        const wrapperRect = wrapper.getBoundingClientRect();
        // Connected production cards always have geometry. The 1px floor
        // keeps the independent module contract deterministic in DOM-only
        // environments that do not perform layout.
        const cardWidth = cardElement.offsetWidth || cardRect.width || 1;
        const cardHeight = cardElement.offsetHeight || cardRect.height || 1;
        const w = wrapper.offsetWidth || wrapperRect.width || cardWidth;
        const h = wrapper.offsetHeight || wrapperRect.height || cardHeight;
        if (!Number.isFinite(scale) || scale <= 1) return null;

        const existing = document.getElementById(MULLIGAN_PREVIEW_ID);
        if (existing) {
            const existingWrapper = existing._mulliganSourceWrapper;
            existingWrapper?._cleanupHover?.({ immediate: true });
            document.getElementById(MULLIGAN_PREVIEW_ID)?.remove();
        }

        const preview = document.createElement('div');
        preview.id = MULLIGAN_PREVIEW_ID;
        preview.className = 'card-hover-preview mulligan-hover-preview';
        preview.setAttribute('aria-hidden', 'true');
        preview.style.left = `${Math.round(cardRect.left)}px`;
        preview.style.top = `${Math.round(cardRect.top)}px`;

        const state = {
            onPointerMove: null,
            onViewportChange: null,
            pointer: null,
            preview,
            cardElement,
            cardStyle: wrapper._mulliganRestingCardStyle ?? cardElement.style.cssText,
            cardClassName: wrapper._mulliganRestingCardClassName ?? cardElement.className,
            wrapperWidth: wrapper.style.width,
            wrapperHeight: wrapper.style.height,
        };
        wrapper._mulliganPreviewState = state;
        preview._mulliganSourceWrapper = wrapper;

        wrapper.style.width = `${w}px`;
        wrapper.style.height = `${h}px`;
        cardElement.style.margin = '0';
        cardElement.style.transformOrigin = 'center center';
        cardElement.style.transition = 'none';
        cardElement.style.pointerEvents = 'none';
        preview.appendChild(cardElement);
        document.body.appendChild(preview);

        const previewOptions = {
            ...(Cards?.HOVER_PREVIEW_OPTIONS || {}),
            baseScale: scale,
            minScale: scale,
            margin: 16,
            origin: 'center center',
            translateYPercent: 0,
        };
        if (typeof Cards?.applyPreviewScale === 'function') {
            Cards.applyPreviewScale(cardElement, 'normal', cardRect, preview, previewOptions);
        } else {
            // Cards.js owns the production path. This physical fallback keeps
            // the module independently testable without stretching pixels.
            cardElement.style.setProperty('--card-unit', `${(cardWidth / 12.29) * scale}px`);
            cardElement.style.setProperty('--card-width', `${cardWidth * scale}px`);
            cardElement.style.setProperty('--card-height', `${cardHeight * scale}px`);
            cardElement.style.setProperty('--card-base-transform', 'scale(1)');
            cardElement.style.setProperty(
                '--card-hover-transform',
                'rotate(0deg) translateY(0%)',
            );
            Cards?.fitCardDescription?.(cardElement, { immediate: true });
            Cards?.positionHoverPreview?.(preview, cardRect, 1);
        }

        state.onPointerMove = (event) => {
            state.pointer = { x: event.clientX, y: event.clientY };
            if (isPointerWithinPreview(state, wrapper, state.pointer)) {
                CardHover?.applyTiltFromPointer?.(
                    cardElement,
                    state.pointer
                );
                return;
            }
            wrapper._cleanupHover?.();
        };
        preview.addEventListener('click', (event) => {
            if (event.button !== 0) return;
            event.preventDefault();
            event.stopPropagation();
            wrapper.click();
        });
        document.addEventListener('pointermove', state.onPointerMove, true);
        state.onViewportChange = () => wrapper._cleanupHover?.({ immediate: true });
        window.addEventListener('resize', state.onViewportChange, { passive: true });
        window.addEventListener('orientationchange', state.onViewportChange, { passive: true });
        return state;
    }

    async function activateMulliganPreview(wrapper, cardElement, card, scale, onReady) {
        const state = mountMulliganPreview(wrapper, cardElement, scale);
        if (!state) return false;
        try {
            const settleRaster = Cards?.settleCardRasterForPresentation;
            let rasterSettled = true;
            if (typeof settleRaster === 'function') {
                rasterSettled = await settleRaster(cardElement, card, {
                    oversample: 1,
                    stableGeometry: true,
                });
            }
            if (rasterSettled !== true) {
                wrapper._cleanupHover?.({ immediate: true });
                return false;
            }
            if (
                wrapper._mulliganPreviewState !== state
                || !wrapper.isConnected
                || !state.preview.isConnected
            ) return false;

            // Fine-pointer hover stays strict: only a successfully revalidated
            // final raster may enter its interactive enlarged state.
            presentMulliganPreview(wrapper, cardElement, onReady);
        } catch (error) {
            if (typeof wrapper._cleanupHover === 'function') {
                wrapper._cleanupHover({ immediate: true });
            } else {
                wrapper.classList.remove('hover-enlarged');
                restoreMulliganPreview(wrapper, cardElement);
            }
            throw error;
        }
        return true;
    }

    function presentMulliganPreview(wrapper, cardElement, onReady) {
        cardElement.classList.remove('card-tilt-disabled');
        wrapper.classList.add('hover-enlarged');
        onReady();
    }

    /**
     * A touch latch is already an explicit inspection request. Mount its exact
     * physical card in the latch event, then let the art well refine at final
     * geometry without giving that optional refinement ownership of the popup.
     */
    function activateTouchMulliganPreview(wrapper, cardElement, card, scale, onReady) {
        if (!mountMulliganPreview(wrapper, cardElement, scale)) return false;

        presentMulliganPreview(wrapper, cardElement, onReady);

        const settleRaster = Cards?.settleCardRasterForPresentation;
        if (typeof settleRaster === 'function') {
            try {
                // False, rejection, and a still-pending art well are all
                // non-destructive here: text/frame inspection is already live.
                void Promise.resolve(settleRaster(cardElement, card, {
                    oversample: 1,
                    stableGeometry: true,
                })).catch(() => undefined);
            } catch (_) {
                // A synchronous raster failure has the same progressive-art
                // semantics as a rejected settlement promise.
            }
        }
        return true;
    }

    function resetMulliganHover(wrapper, cardElement, { immediate = false } = {}) {
        wrapper.style.removeProperty('--hover-scale');
        const state = wrapper._mulliganPreviewState;
        if (!state || !cardElement) {
            wrapper.style.width = '';
            wrapper.style.height = '';
            return;
        }

        removePreviewGuards(state);
        restoreMulliganPreview(wrapper, cardElement);
    }

    function cleanupCardHovers(root, { immediate = true } = {}) {
        if (!root) return;
        root.querySelectorAll('.mulligan-card-wrapper').forEach(wrapper => {
            if (typeof wrapper._cleanupHover === 'function') wrapper._cleanupHover({ immediate });
        });
    }

    function clearSharedCardPreview() {
        const preview = document.getElementById('card-hover-preview');
        if (!preview) return;
        try {
            Cards?.clearHoverPreview?.();
        } catch (error) {
            console.error('[Mulligan] failed to clear stale card preview:', error);
        } finally {
            if (preview.isConnected) preview.remove();
        }
    }

    // State
    let selectedCards = new Set();
    let requiredTutorialSelections = new Set();
    let overlayElement = null;
    let timerInterval = null;
    let mulliganTimeoutMs = 30000; // 30 seconds default
    let authoritativeDeadlineAt = null;
    let localDeadlineAt = null;
    // The server sends `mulliganTimeoutMs: null` for a mulligan with no
    // deadline: the scripted tutorial's, which is its first lesson.
    let untimed = false;
    // Token for a reveal awaiting the asset-preloader gate (≤450ms). While
    // set, isActive() reports true so socket-handler never double-shows.
    let pendingReveal = null;
    // The show whose staging the unconditional backstop below may release.
    let stagingOwner = null;
    // The normal battlefield hand must remain hidden while an authoritative
    // post-mulligan hand is warming. Tokens prevent a late completion from an
    // older game/reveal from exposing a newer hand.
    let handHoldToken = null;
    // Whether the opponent has already kept, from the latest authoritative
    // status (show / updateOpponentStatus). Read at the press: it decides
    // whether the wait after Keep is a wait for the opponent at all.
    let opponentReady = false;
    // The overlay dissolves (fades out) to reveal the battlefield instead of
    // a hard display:none pop — this IS the battle-entry reveal now, replacing
    // the old mistimed black veil. Timer is cancelled if a re-show intervenes.
    const MULLIGAN_LEAVE_MS = 380;
    let leaveTimer = null;
    let controlsRevealTimer = null;

    function hideControlsDuringInspection() {
        if (controlsRevealTimer) {
            clearTimeout(controlsRevealTimer);
            controlsRevealTimer = null;
        }
        overlayElement?.classList.add(MULLIGAN_INSPECTION_CLASS);
    }

    function revealControlsAfterInspection({ immediate = false } = {}) {
        if (controlsRevealTimer) {
            clearTimeout(controlsRevealTimer);
            controlsRevealTimer = null;
        }
        if (immediate || prefersReducedMotion()) {
            overlayElement?.classList.remove(MULLIGAN_INSPECTION_CLASS);
            return;
        }
        controlsRevealTimer = setTimeout(() => {
            controlsRevealTimer = null;
            overlayElement?.classList.remove(MULLIGAN_INSPECTION_CLASS);
        }, MULLIGAN_HOVER_LEAVE_MS);
    }

    function uiText(key, fallback, replacements = {}) {
        const translated = i18n?.t?.(key, replacements);
        /* The FALLBACK is interpolated too. It is returned verbatim whenever
           i18n has not resolved — the normal case on a cold load — and the
           ui.json wording these fallbacks mirror carries `{{name}}`
           placeholders, so a user could be shown a literal `{{count}}`. Both
           brace styles are handled because the copy uses both. */
        const source = translated && translated !== key ? translated : fallback;
        return Object.entries(replacements || {}).reduce(
            (text, [name, value]) => String(text)
                .split(`{{${name}}}`).join(String(value))
                .split(`{${name}}`).join(String(value)),
            String(source == null ? key : source),
        );
    }

    function getRedrawCountText() {
        /* Just "Redraw". The count is already on screen — the selected cards
           are the ones marked — so spelling it into the button only made the
           label long enough to shrink on a phone, which is how a primary
           action ended up unreadable. The counted strings stay in the
           translation file for any surface that wants them. */
        return uiText('mulligan.redraw', 'Redraw');
    }

    function getTutorialForcedDiscardIds(tutorialData) {
        if (Array.isArray(tutorialData?.mulliganForcedDiscards)) {
            return tutorialData.mulliganForcedDiscards.filter(id => typeof id === 'string' && id.length > 0);
        }

        if (typeof tutorialData?.mulliganForcedDiscard === 'string' && tutorialData.mulliganForcedDiscard.length > 0) {
            return [tutorialData.mulliganForcedDiscard];
        }

        return [];
    }

    function getTutorialSelectionText(remaining, total) {
        if (remaining === 0) {
            return uiText('mulligan.redraw', 'Redraw');
        }
        if (remaining === total && total === 2) {
            return uiText('mulligan.selectBothCards', 'Select Both Cards');
        }
        if (remaining === total) {
            return uiText('mulligan.selectCardCount', `Select ${total} Cards`, { count: total });
        }
        return uiText('mulligan.selectMoreCount', `Select ${remaining} More`, { count: remaining });
    }

    function getRemainingTutorialSelections() {
        let remaining = 0;
        requiredTutorialSelections.forEach(instanceId => {
            if (!selectedCards.has(instanceId)) {
                remaining++;
            }
        });
        return remaining;
    }

    function getDiscardIdsForConfirm() {
        if (requiredTutorialSelections.size > 0) {
            return Array.from(requiredTutorialSelections);
        }

        return Array.from(selectedCards);
    }

    function setConfirmButtonText(confirmBtn, text) {
        if (!confirmBtn) return;
        // The label describes live selection state. Leaving the static
        // `mulligan.keepHand` marker in place lets a later page-wide i18n pass
        // overwrite that state (most visibly during the tutorial).
        confirmBtn.removeAttribute('data-i18n');
        confirmBtn.textContent = text;
    }

    function updateTutorialConfirmButton(confirmBtn) {
        const remaining = getRemainingTutorialSelections();
        const total = requiredTutorialSelections.size;

        confirmBtn.disabled = remaining > 0;
        setConfirmButtonText(confirmBtn, getTutorialSelectionText(remaining, total));
    }

    /**
     * Initialize the mulligan overlay
     */
    function init() {
        // Create overlay if it doesn't exist
        if (!document.getElementById('mulligan-overlay')) {
            createOverlayElement();
        }
        overlayElement = document.getElementById('mulligan-overlay');
    }

    function holdPlayerHand() {
        const token = {};
        handHoldToken = token;
        const playerHand = document.getElementById('player-hand');
        if (playerHand) playerHand.style.visibility = 'hidden';
        return token;
    }

    function releasePlayerHand(token = handHoldToken) {
        if (!handHoldToken || token !== handHoldToken) return false;
        handHoldToken = null;
        const playerHand = document.getElementById('player-hand');
        if (playerHand) playerHand.style.visibility = '';
        return true;
    }

    /**
     * Create the mulligan overlay DOM element
     */
    function createOverlayElement() {
        const overlay = document.createElement('div');
        overlay.id = 'mulligan-overlay';
        overlay.innerHTML = `
            <div class="mulligan-timer" id="mulligan-timer" role="timer"
                 aria-label="${uiText('mulligan.timeRemaining', 'Time remaining')}">
                <span class="mulligan-timer-value">30</span>
                <span class="mulligan-timer-bar" aria-hidden="true"></span>
            </div>
            <div class="mulligan-header">
                <h2 data-i18n="mulligan.title">Choose Cards to Mulligan</h2>
                <p data-i18n="mulligan.description" data-i18n-mobile="mulligan.descriptionTap">Select cards to discard and draw replacements. Click a card to mark it for mulligan.</p>
            </div>
            <div class="mulligan-cards" id="mulligan-cards">
                <!-- Cards will be rendered here -->
            </div>
            <div class="mulligan-status">
                <div class="opponent-status" id="opponent-mulligan-status">
                    <span class="spinner"></span>
                    <span data-i18n="mulligan.opponentDeciding">Opponent is deciding...</span>
                </div>
            </div>
            <div class="mulligan-buttons">
                <button class="mulligan-btn confirm" id="mulligan-confirm-btn" data-i18n="mulligan.keepHand">Keep Hand</button>
            </div>
        `;
        document.body.appendChild(overlay);

        // Attach event listeners
        const confirmBtn = document.getElementById('mulligan-confirm-btn');
        confirmBtn.addEventListener('click', () => {
            const guard = MoonfallClickGuard;
            if (guard && typeof guard.guard === 'function') {
                guard.guard(confirmBtn, async () => confirmMulligan()).catch((err) => {
                    console.error('[Mulligan] guarded confirm failed:', err);
                });
            } else {
                confirmMulligan();
            }
        });

        // Apply translations if i18n is available
        if (i18n && typeof i18n.translatePage === 'function') {
            i18n.translatePage();
        }
    }

    /**
     * Show the mulligan overlay with the player's starting hand
     * @param {Array} hand - Array of card objects in the player's hand
     * @param {boolean} opponentReady - Whether the opponent has completed mulligan
     */
    function show(hand, opponentReady = false, {
        timeoutMs,
        deadlineAt,
        serverNow,
    } = {}) {
        // The countdown derives from the server's authoritative auto-complete
        // deadline when provided; the 30s literal is only a fallback. An
        // explicit null is not a missing value: that mulligan has no deadline,
        // so it shows no countdown and only the player's choice closes it.
        untimed = timeoutMs === null;
        if (Number.isFinite(timeoutMs) && timeoutMs > 0) {
            mulliganTimeoutMs = timeoutMs;
        }
        if (untimed) {
            authoritativeDeadlineAt = null;
            localDeadlineAt = null;
        } else if (Number.isFinite(deadlineAt) && Number.isFinite(serverNow)) {
            const candidateLocalDeadline = Date.now() + (deadlineAt - serverNow);
            if (authoritativeDeadlineAt !== deadlineAt || !Number.isFinite(localDeadlineAt)) {
                authoritativeDeadlineAt = deadlineAt;
                localDeadlineAt = candidateLocalDeadline;
            } else {
                // A later authoritative snapshot may refine clock skew, but a
                // repeated/stale payload must never extend the original clock.
                localDeadlineAt = Math.min(localDeadlineAt, candidateLocalDeadline);
            }
        } else {
            authoritativeDeadlineAt = null;
            localDeadlineAt = Date.now() + mulliganTimeoutMs;
        }
        init();
        // Decided before the overlay is staged, so a countdown that does not
        // apply is never painted, not even for a frame.
        const timerEl = document.getElementById('mulligan-timer');
        if (timerEl) timerEl.hidden = untimed;
        revealControlsAfterInspection({ immediate: true });
        clearSharedCardPreview();
        selectedCards.clear();
        requiredTutorialSelections.clear();
        overlayElement.classList.remove('mulligan-raster-failed');
        overlayElement.querySelector('.mulligan-raster-failure')?.remove();
        const existingConfirm = document.getElementById('mulligan-confirm-btn');
        if (existingConfirm) {
            existingConfirm.hidden = false;
            // A press still answered through the last dissolve belongs to that
            // mulligan (whenOverlayGone): its late release must not re-enable
            // a button this show configures, nor swallow the next press.
            MoonfallClickGuard?.forceRelease?.(existingConfirm);
        }

        // Check if in tutorial mode
        const isTutorial = Tutorial?.isActive?.() || false;

        // Get forced discard card from tutorial data (if in tutorial)
        const tutorialData = isTutorial ? Tutorial?.getData?.() : null;
        const forcedDiscardIds = getTutorialForcedDiscardIds(tutorialData);
        const forcedDiscardIdSet = new Set(forcedDiscardIds);

        console.log('[Mulligan] Tutorial check:', {
            isTutorial,
            tutorialExists: !!Tutorial,
            isActiveFunc: typeof Tutorial?.isActive,
            getDataFunc: typeof Tutorial?.getData,
            tutorialData: tutorialData ? Object.keys(tutorialData) : null,
            forcedDiscardIds
        });

        // Render cards
        const cardsContainer = document.getElementById('mulligan-cards');
        cleanupCardHovers(cardsContainer);
        cardsContainer.innerHTML = '';

        hand.forEach((card, index) => {
            const wrapper = document.createElement('div');
            wrapper.className = 'mulligan-card-wrapper';
            wrapper.dataset.instanceId = card.instanceId;
            wrapper.dataset.index = index;

            // Check if this is the forced discard card in tutorial
            const isForcedCard = forcedDiscardIdSet.has(card.id);
            if (isTutorial && isForcedCard) {
                requiredTutorialSelections.add(card.instanceId);
            }

            console.log(`[Mulligan] Card ${index}: id=${card.id}, name=${card.name}, isForcedCard=${isForcedCard}, forcedDiscardIds=${forcedDiscardIds.join(',')}`);

            // In tutorial mode, grey out cards that aren't the forced discard
            if (isTutorial && forcedDiscardIds.length > 0 && !isForcedCard) {
                wrapper.classList.add('tutorial-disabled');
            }

            // Highlight the forced card in tutorial
            if (isTutorial && isForcedCard) {
                wrapper.classList.add('tutorial-forced-select');
            }

            const isSelectable = !isTutorial || forcedDiscardIds.length === 0 || isForcedCard;
            wrapper.setAttribute('role', 'button');
            wrapper.setAttribute('aria-label', card.name);
            wrapper.setAttribute('aria-pressed', 'false');
            wrapper.tabIndex = isSelectable ? 0 : -1;
            if (!isSelectable) wrapper.setAttribute('aria-disabled', 'true');

            // Use the existing card creation function if available
            let cardElement;
            if (Cards && typeof Cards.createCardElement === 'function') {
                cardElement = Cards.createCardElement(
                    card,
                    `mulligan-card-${index}`,
                    { rasterReserveScale: MULLIGAN_RASTER_RESERVE_SCALE, touchPreview: false },
                );
            } else {
                // Fallback: create a basic card element
                cardElement = document.createElement('div');
                cardElement.className = 'card card-front';
                cardElement.textContent = card.name;
            }

            wrapper.appendChild(cardElement);
            const selectionMark = document.createElement('span');
            selectionMark.className = 'mulligan-selection-mark';
            selectionMark.setAttribute('aria-hidden', 'true');
            selectionMark.textContent = '✕';
            wrapper.appendChild(selectionMark);
            // Capture the resting inline style before CardTilt's pointer state
            // can write transient glare/rotation variables. Delayed popup
            // cleanup must restore this baseline, not the entry-frame hover.
            wrapper._mulliganRestingCardStyle = cardElement.style.cssText;
            wrapper._mulliganRestingCardZoom = cardElement.style.zoom || '';
            wrapper._mulliganRestingCardClassName = cardElement.className;

            // The wrapper preserves the row footprint while the same card node
            // moves into the body-level physical inspection popup.
            if (!isTutorial || isForcedCard) {
                let enterTimer = null;  // delay before enlarging
                let leaveTimer = null;  // debounce spurious leave events
                let hoverGeneration = 0;
                let pointerInside = false;

                const cleanupHover = ({ immediate = false } = {}) => {
                    pointerInside = false;
                    hoverGeneration += 1;
                    if (enterTimer) { clearTimeout(enterTimer); enterTimer = null; }
                    if (leaveTimer) { clearTimeout(leaveTimer); leaveTimer = null; }

                    const wasActive = wrapper.classList.contains('hover-enlarged')
                        || cardElement.classList.contains('hover-active');
                    const wasInspecting = overlayElement?.classList.contains(
                        MULLIGAN_INSPECTION_CLASS,
                    );
                    if (wasActive && Cards?.setStellarAudioHover) {
                        Cards.setStellarAudioHover(cardElement, false);
                    }
                    if (wasActive || wasInspecting) {
                        revealControlsAfterInspection({ immediate });
                    }

                    wrapper.classList.remove('hover-enlarged');
                    if (CardHover?.resetCardTilt) {
                        CardHover.resetCardTilt(cardElement);
                    }
                    resetMulliganHover(wrapper, cardElement, { immediate });
                    cardElement.classList.remove('hover-active');
                    cardElement.style.removeProperty('--card-hover-transform');
                    const hint = cardElement.querySelector('.card-hint');
                    if (hint) {
                        hint.textContent = '';
                        hint.classList.remove('visible-hint');
                    }
                    Cards?.hideSideHint?.(cardElement);
                };
                wrapper._cleanupHover = cleanupHover;

                // Touch has no hover: the tap-latch drives this same mouseenter
                // path so the mulligan cards can be read before they are kept
                // or replaced. Without it the whole first turn is a blind pick.
                /* Mulligan owns one deterministic three-step cadence on touch:
                   inspect, select, deselect. The first tap is swallowed after
                   opening the preview, the second follows TouchPreview's
                   ordinary two-tap action path, and a selected card bypasses
                   inspection so its next tap immediately deselects. */
                TouchPreview?.attach?.(wrapper, {
                    shouldPreview: () => !wrapper.classList.contains('selected'),
                    /* The mulligan is the ONE surface whose second tap has a
                       real, guessable consequence, so it is the one that gets
                       to teach it — and it says what that tap actually does.
                       The old shared copy promised "tap again to play", which
                       is not what happens to a card you are mulliganing. */
                    hint: {
                        key: 'game.tapAgainToDiscard',
                        fallback: 'Tap again to discard',
                        /* An affordance, not a tutorial: discarding is the one
                           consequence here and every card needs to say it, not
                           just the first one the player ever taps twice. */
                        persistent: true,
                    },
                });

                wrapper.addEventListener('mouseenter', (event) => {
                    // A tap makes the browser synthesize a TRUSTED enter/leave
                    // pair around the touch point. The leave half arrives a beat
                    // after the latch opened and tore the enlargement straight
                    // back down; only the latch's own synthetic events
                    // (isTrusted === false) may drive hover on a touch device.
                    if (event?.isTrusted !== false && TouchPreview?.isSuppressingMouse?.()) return;
                    if (leaveTimer) { clearTimeout(leaveTimer); leaveTimer = null; }
                    if (wrapper.classList.contains('selected')) return;
                    if (wrapper._mulliganPreviewState || wrapper.classList.contains('hover-enlarged')) return;
                    pointerInside = true;
                    const generation = ++hoverGeneration;
                    const touchLatch = event?.isTrusted === false
                        && TouchPreview?.isLatched?.(wrapper) === true;

                    const previewIsCurrent = () => generation === hoverGeneration
                        && pointerInside
                        && wrapper.isConnected
                        && !wrapper.classList.contains('selected');

                    const startPreview = async () => {
                        try {
                            if (!previewIsCurrent()) return;

                            // Desktop hover retains both readiness gates. An
                            // exact TouchPreview latch skips only this pre-mount
                            // gate so the first tap cannot look unresponsive.
                            if (!touchLatch) {
                                const prepareRaster = Cards?.prepareCardRasterForInteraction;
                                if (typeof prepareRaster === 'function') {
                                    let rasterReady = false;
                                    try {
                                        rasterReady = await prepareRaster(cardElement, 'hover');
                                    } catch (_) {
                                        rasterReady = false;
                                    }
                                    if (rasterReady !== true || !previewIsCurrent()) return;
                                }
                            }

                            let targetScale = 3.5;
                            if (Cards && typeof Cards.calculateHoverScale === 'function') {
                                targetScale = Cards.calculateHoverScale(cardElement, {
                                    baseScale: 3.5, minScale: 1.6, margin: 16
                                });
                            }

                            hideControlsDuringInspection();

                            const activateHover = () => {
                                cardElement.classList.add('hover-active');
                                if (Cards?.setStellarAudioHover) {
                                    Cards.setStellarAudioHover(cardElement, true);
                                }
                                if (Cards && typeof Cards.updateCardHint === 'function') {
                                    Cards.updateCardHint(cardElement);
                                }
                            };

                            wrapper.style.setProperty('--hover-scale', targetScale);
                            if (touchLatch) {
                                activateTouchMulliganPreview(
                                    wrapper,
                                    cardElement,
                                    card,
                                    targetScale,
                                    activateHover,
                                );
                                return;
                            }
                            await activateMulliganPreview(
                                wrapper,
                                cardElement,
                                card,
                                targetScale,
                                activateHover,
                            );
                        } catch (error) {
                            cleanupHover({ immediate: true });
                            console.error('[Mulligan] failed to activate card preview:', error);
                        }
                    };

                    if (touchLatch) {
                        // startPreview runs synchronously until its desktop-only
                        // await, so the physical popup mounts in this latch event.
                        void startPreview();
                        return;
                    }

                    // Fine pointers keep hover-intent protection even when a
                    // test or assistive surface dispatches an untrusted enter.
                    enterTimer = setTimeout(() => {
                        enterTimer = null;
                        void startPreview();
                    }, 275);
                });

                wrapper.addEventListener('mouseleave', (event) => {
                    if (event?.isTrusted !== false && TouchPreview?.isSuppressingMouse?.()) return;
                    pointerInside = false;
                    hoverGeneration += 1;
                    // Cancel pending enlarge if mouse left before delay elapsed
                    if (enterTimer) { clearTimeout(enterTimer); enterTimer = null; }

                    const exitPointer = { x: event.clientX, y: event.clientY };
                    leaveTimer = setTimeout(() => {
                        leaveTimer = null;
                        const state = wrapper._mulliganPreviewState;
                        if (isPointerWithinPreview(state, wrapper, state?.pointer || exitPointer)) return;
                        cleanupHover();
                    }, 30);
                });

                // Store refs for use in toggleCardSelection
                wrapper._cardElement = cardElement;
            }

            // In tutorial, only allow clicking the forced discard card
            // In normal game, allow clicking any card
            if (isSelectable) {
                const selectCard = () => toggleCardSelection(wrapper, card.instanceId);
                wrapper.addEventListener('click', selectCard);
                wrapper.addEventListener('keydown', (event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    event.preventDefault();
                    selectCard();
                });
            }
            cardsContainer.appendChild(wrapper);
        });

        // Update opponent status
        updateOpponentStatus(opponentReady);

        // Configure buttons based on mode
        const confirmBtn = document.getElementById('mulligan-confirm-btn');

        if (isTutorial && requiredTutorialSelections.size > 0) {
            // In tutorial with forced mulligan, only allow continuing after selecting every forced card.
            updateTutorialConfirmButton(confirmBtn);
        } else if (isTutorial) {
            setConfirmButtonText(confirmBtn, uiText('mulligan.continue', 'Continue'));
            confirmBtn.disabled = false;
        } else {
            // Normal game: same single button handles keep/redraw.
            setConfirmButtonText(confirmBtn, uiText('mulligan.keepHand', 'Keep Hand'));
            confirmBtn.disabled = false;
        }

        // Hide the normal hand so cards aren't duplicated behind the overlay
        holdPlayerHand();

        const reveal = () => {
            // Cancel any in-flight dissolve so a re-show isn't torn down by it.
            if (leaveTimer) { clearTimeout(leaveTimer); leaveTimer = null; }
            overlayElement.classList.remove('mulligan-leaving');
            overlayElement.classList.remove('mulligan-raster-staging');
            overlayElement.classList.add('active');
            startTimer();

            // Cards are built while the overlay is display:none, and the
            // visible-first art gate can outlast createCardElement's short
            // zero-layout retry window. Fit once at the real visibility
            // boundary so long descriptions are settled before any hover.
            if (Cards?.fitCardDescription) {
                overlayElement.querySelectorAll('#mulligan-cards .card').forEach(card => {
                    Cards.fitCardDescription(card);
                });
            }

            // Deal the starting hand in once the overlay is actually visible
            // (after the visible-first art gate), not at build time. A bigger
            // step than the collection grid — only ~3-5 cards, each a hero beat.
            FX?.staggerChildren?.(document.getElementById('mulligan-cards'), { step: 90 });

            // The match's first screen is up: lift the match loading screen in
            // this same task, so the battle appears whole with its hand
            // dealing in (ui/match-loading.js). A no-op on any later show.
            MatchLoading?.presented?.('mulligan');

            // The overlay's cards appear after Tutorial.start()'s delayed first
            // tip, so the cue layer needs a nudge once the wrappers exist.
            if (isTutorial) {
                Tutorial?.refreshCues?.();
            }

            // Update card hover positions for the newly visible mulligan cards
            if (CardHover?.updatePositions) {
                // Slight delay to ensure layout is computed
                setTimeout(() => CardHover.updatePositions(), 50);
            }

            console.log('[Mulligan] Showing mulligan UI with', hand.length, 'cards, tutorial:', isTutorial, 'forcedDiscards:', forcedDiscardIds);
        };

        // Explicit failure path. The per-card raster gate owns correctness, so
        // if any card cannot settle its immutable presentation raster we refuse
        // to reveal a partial or half-rendered hand. Rather than a synthetic
        // "healthy" mulligan, clear the hand and surface an actionable error.
        const showRasterFailure = () => {
            if (leaveTimer) { clearTimeout(leaveTimer); leaveTimer = null; }
            overlayElement.classList.remove('mulligan-leaving', 'mulligan-raster-staging');
            overlayElement.classList.add('active', 'mulligan-raster-failed');
            const cards = document.getElementById('mulligan-cards');
            if (cards) cards.replaceChildren();
            overlayElement.querySelector('.mulligan-raster-failure')?.remove();
            const failure = document.createElement('div');
            failure.className = 'mulligan-raster-failure';
            const alertEl = document.createElement('p');
            alertEl.className = 'mulligan-raster-alert';
            alertEl.setAttribute('role', 'alert');
            alertEl.textContent = uiText(
                'mulligan.rasterFailed',
                "Your opening hand couldn't be prepared. To avoid showing a broken hand, the mulligan was stopped.",
            );
            const returnBtn = document.createElement('button');
            returnBtn.type = 'button';
            returnBtn.className = 'mulligan-raster-return';
            returnBtn.textContent = uiText('gameEnd.returnToLobby', 'Go to Lobby');
            // A raster/canvas subsystem failure is best recovered by a clean
            // re-initialization; reload routes back through lobby/resume.
            returnBtn.addEventListener('click', () => { window.location.reload(); });
            failure.appendChild(alertEl);
            failure.appendChild(returnBtn);
            overlayElement.appendChild(failure);
            MatchLoading?.presented?.('mulligan-failed');
            console.error('[Mulligan] Raster presentation gate failed; showing explicit failure instead of a partial hand.');
        };

        // Lay the real overlay out invisibly while every card settles at its
        // final geometry. `visibility:hidden` preserves measurement, so the
        // shared raster path can install each immutable hover-reserve paint
        // before the overlay is presented. The whole hand then usually appears
        // in one frame; past the hard reveal cap it appears text-first with
        // animated art-well placeholders while slow art keeps settling
        // (docs/asset-loading.md law 2 — no unbounded user-visible waits).
        const cardElements = Array.from(
            cardsContainer.querySelectorAll('.mulligan-card-wrapper > .card')
        );
        const settleRaster = Cards?.settleCardRasterForPresentation;
        let rasterGate = null;
        if (typeof settleRaster === 'function' && cardElements.length > 0) {
            if (leaveTimer) { clearTimeout(leaveTimer); leaveTimer = null; }
            overlayElement.classList.remove('mulligan-leaving');
            overlayElement.classList.add('active', 'mulligan-raster-staging');
            if (Cards?.fitCardDescription) {
                cardElements.forEach(cardElement => {
                    Cards.fitCardDescription(cardElement);
                });
            }
            rasterGate = Promise.allSettled(cardElements.map((cardElement, index) => {
                try {
                    return Promise.resolve(settleRaster(
                        cardElement,
                        hand[index],
                        { oversample: MULLIGAN_RASTER_RESERVE_SCALE, stableGeometry: true },
                    ));
                } catch (_) {
                    return Promise.resolve(false);
                }
            }));
        }

        // Visible-first preload gate still covers the rest of the battle
        // surface. Its bounded warm is an optimization; the per-card raster
        // gate above owns correctness and may continue through a slow but valid
        // asset resolution instead of exposing a partial mulligan.
        const gate = AssetPreloader?.gateReveal?.();
        const assetGate = (gate && typeof gate.then === 'function')
            ? Promise.resolve(gate).catch(() => undefined)
            : null;
        /* On a match start the board behind the veil — both card-back piles,
           the opponent's fan, both portraits — is being built under the match
           loading screen too. The hand waits for it inside the same cap, so the
           whole battle presents in one frame (ui/match-loading.js). Null when
           there is no pending arrival: a later show waits for nothing new. */
        const board = MatchLoading?.whenBoardReady?.();
        const boardGate = (board && typeof board.then === 'function')
            ? Promise.resolve(board).catch(() => undefined)
            : null;
        if (boardGate && rasterGate) MatchLoading?.track?.(rasterGate);
        if (rasterGate || assetGate || boardGate) {
            const token = {};
            pendingReveal = token;
            stagingOwner = token;
            const gatesSettled = Promise.all([
                rasterGate || Promise.resolve(null),
                assetGate || Promise.resolve(undefined),
                boardGate || Promise.resolve(undefined),
            ]).then(([rasterResults]) => ({ rasterResults, timedOut: false }));
            // Hard cap: a slow-but-valid asset resolution may not hold the hand
            // hostage. Past the cap the hand reveals text-first and each art
            // well keeps its placeholder until its own paint settles.
            const revealCap = new Promise((resolve) => {
                setTimeout(() => resolve({ rasterResults: null, timedOut: true }), MULLIGAN_REVEAL_CAP_MS);
            });
            /* THE GATE MUST BE ABLE TO PASS WITHOUT THE PROMISE CHAIN.
               Everything below hangs off one `.then`, so a chain that never
               settles leaves `mulligan-raster-staging` on the overlay forever —
               and that class is `visibility: hidden` on `.mulligan-buttons`, so
               the player is left staring at a mulligan they cannot confirm. It
               is not hypothetical: instrumenting the live surface in headless
               WebKit caught the race armed twice and settled ZERO times, with
               no page error, while the confirm button sat at
               `visibility: hidden` for 150 seconds. The cap above is a
               `setTimeout` INSIDE a promise, so it protects against a slow gate
               and not against a chain that never runs at all.
               This release does not depend on any promise: past the cap the
               staging class comes off, unconditionally, and the worst case is
               the reveal beat it would have played anyway.
               Contract: tests/unit/ui/mulliganStagingRelease.test.js. */
            setTimeout(() => {
                // This show's own backstop: a later show's staging has its own.
                if (stagingOwner !== token) return;
                if (!overlayElement?.classList.contains('mulligan-raster-staging')) return;
                overlayElement.classList.remove('mulligan-raster-staging');
                MatchLoading?.presented?.('mulligan-cap');
            }, MULLIGAN_REVEAL_CAP_MS + 250);
            Promise.race([gatesSettled, revealCap]).then(({ rasterResults, timedOut }) => {
                if (pendingReveal !== token) return; // hidden or superseded meanwhile
                pendingReveal = null;
                // When the gate finishes in time it owns correctness: any card
                // that did not settle its immutable presentation raster
                // (resolved false or rejected) means we must not expose the
                // hand. A post-cap reveal instead trusts the per-card loader's
                // own truthful placeholder/unavailable-art handling.
                const rasterFailed = !timedOut
                    && Array.isArray(rasterResults)
                    && !rasterResults.every(r => r.status === 'fulfilled' && r.value !== false);
                if (rasterFailed) {
                    showRasterFailure();
                    return;
                }
                reveal();
            });
        } else {
            reveal();
        }
    }

    /**
     * Hide the mulligan overlay
     */
    function hide({ immediate = false, keepHandHidden = false } = {}) {
        pendingReveal = null;
        const overlay = overlayElement;
        /* A card enlarged by a tap holds TouchPreview's latch and its
           body-level "Tap again to discard" label. Closing the overlay under
           it (the deadline, Keep) left that label standing over the battle
           and the tutorial's next lesson; the latch goes with the overlay. */
        const latchedCard = TouchPreview?.latchedElement?.();
        if (latchedCard && overlay?.contains(latchedCard)) {
            TouchPreview?.cancelActivePress?.();
        }
        cleanupCardHovers(overlay);
        if (leaveTimer) {
            clearTimeout(leaveTimer);
            leaveTimer = null;
        }
        const wasRasterStaging = overlay?.classList.contains('mulligan-raster-staging');
        overlay?.classList.remove('mulligan-raster-staging');
        if (!immediate
            && !prefersReducedMotion()
            && !wasRasterStaging
            && overlay
            && overlay.classList.contains('active')) {
            // Graceful dissolve: keep it laid out (via .active) while it fades,
            // then drop it. This reveal replaces the old black battle veil.
            overlay.classList.add('mulligan-leaving');
            leaveTimer = setTimeout(() => {
                overlay.classList.remove('active', 'mulligan-leaving');
                leaveTimer = null;
            }, MULLIGAN_LEAVE_MS);
        } else if (overlay) {
            overlay.classList.remove('active', 'mulligan-leaving');
        }
        if (immediate) revealControlsAfterInspection({ immediate: true });
        if (!keepHandHidden) releasePlayerHand();
        stopTimer();
        selectedCards.clear();
        requiredTutorialSelections.clear();
        console.log('[Mulligan] Hiding mulligan UI');
    }

    /**
     * Show the "waiting for opponent" banner after player confirms mulligan
     */
    function showWaitingBanner() {
        hideWaitingBanner();
        const banner = document.createElement('div');
        banner.id = 'mulligan-waiting-banner';
        banner.className = 'game-notification';
        banner.textContent = uiText('mulligan.waiting', 'Waiting for opponent to finish mulliganing...');
        document.body.appendChild(banner);
    }

    /**
     * Hide the "waiting for opponent" banner
     */
    function hideWaitingBanner() {
        const banner = document.getElementById('mulligan-waiting-banner');
        if (banner) banner.remove();
    }

    /**
     * Toggle card selection for mulligan
     * @param {HTMLElement} wrapper - The card wrapper element
     * @param {string} instanceId - The card's instance ID
     */
    function toggleCardSelection(wrapper, instanceId) {
        const isTutorial = Tutorial?.isActive?.() || false;

        if (selectedCards.has(instanceId)) {
            selectedCards.delete(instanceId);
            wrapper.classList.remove('selected');
        } else {
            selectedCards.add(instanceId);
            wrapper.classList.add('selected');

            // Deactivate hover so the X overlay is visible immediately
            const cardEl = wrapper._cardElement || wrapper.querySelector('.card');
            if (cardEl) {
                if (Cards && typeof Cards.setStellarHover === 'function') {
                    Cards.setStellarHover(cardEl, false);
                }
            }
            wrapper._cleanupHover?.({ immediate: true });
        }
        wrapper.setAttribute('aria-pressed', String(selectedCards.has(instanceId)));

        // Update confirm button text and state
        const confirmBtn = document.getElementById('mulligan-confirm-btn');

        if (isTutorial && requiredTutorialSelections.size > 0) {
            // In tutorial with forced mulligan - enable button only when all required cards are selected
            updateTutorialConfirmButton(confirmBtn);
        } else if (selectedCards.size > 0) {
            setConfirmButtonText(confirmBtn, getRedrawCountText(selectedCards.size));
        } else {
            setConfirmButtonText(confirmBtn, isTutorial
                ? uiText('mulligan.continue', 'Continue')
                : uiText('mulligan.keepHand', 'Keep Hand'));
        }

        // The tutorial cue moves from the forced cards to the confirm button
        // as selections change.
        if (isTutorial) {
            Tutorial?.refreshCues?.();
        }
    }

    /**
     * Confirm mulligan and send to server
     */
    function confirmMulligan() {
        if (requiredTutorialSelections.size > 0 && getRemainingTutorialSelections() > 0) {
            return;
        }

        const discardIds = getDiscardIdsForConfirm();
        console.log('[Mulligan] Confirming mulligan with discards:', discardIds);

        if (SocketHandler && typeof SocketHandler.confirmMulligan === 'function') {
            SocketHandler.confirmMulligan(discardIds);
        } else {
            console.error('[Mulligan] SocketHandler.confirmMulligan not available');
        }

        // Notify tutorial system
        if (Tutorial?.isActive?.()) {
            Tutorial.onGameEvent('mulliganComplete');
        }

        hide({ keepHandHidden: true });
        showWaitingBannerIfOpponentDeciding();
        return whenOverlayGone();
    }

    /* ONE LOADING STATE AFTER KEEP, AND ONLY A TRUE ONE (round 15, 28
       September 2026). Every recorded keep in int15 (12 of 12, four seats)
       put two centred toasts on screen at once, "Processing action..." over
       "Waiting for opponent to finish mulliganing...", for 180-900 ms, and
       then took both down as the hand arrived. The opponent had already
       kept: the first view after keep was the first turn. Waiting for an
       opponent who is ready is not true, so the line shows only while the
       opponent is still deciding. It is then the wait's one loading state:
       SocketHandler.confirmMulligan keeps the keep's processing chrome silent
       (isOpponentReady). It also earns its place past the shared grace
       (mulligan.css), so a keep answered inside the grace shows nothing. */
    function showWaitingBannerIfOpponentDeciding() {
        if (opponentReady) {
            hideWaitingBanner();
            return;
        }
        showWaitingBanner();
    }

    /** Whether the opponent had already kept at the latest status. */
    function isOpponentReady() {
        return opponentReady;
    }

    /* THE PRESS STAYS ANSWERED UNTIL THE OVERLAY HAS GONE (25 September 2026,
       WebKit phone: "slow acknowledgement 498 ms"). The click guard's pending
       look lived 2 ms: confirmMulligan() is synchronous, so the guard settled
       in the same task and the look was never painted, and the next frame
       the page managed came ~450 ms later. Held through the dissolve, the look
       is in the first frame after the press and never comes off a button
       still on screen: when it clears, the overlay has gone. Bounded by the
       dissolve itself; with no dissolve the overlay goes in this same task. */
    function whenOverlayGone() {
        if (!overlayElement?.classList.contains('mulligan-leaving')) return Promise.resolve();
        return new Promise((resolve) => { setTimeout(resolve, MULLIGAN_LEAVE_MS); });
    }

    /**
     * Update opponent mulligan status display
     * @param {boolean} ready - Whether opponent has completed mulligan
     */
    function updateOpponentStatus(ready) {
        opponentReady = ready === true;
        const statusEl = document.getElementById('opponent-mulligan-status');
        if (!statusEl) return;

        /* RECONCILE, NEVER REBUILD. A snapshot that changes nothing (a
           same-match resume keeping the mulligan, the early state reconciled
           after gameStarted) used to tear the spinner down and build a new
           one, its turn restarting mid-rotation, and re-translate the page.
           Only a real change rewrites the line. */
        const next = ready ? 'ready' : 'deciding';
        if (statusEl.dataset.opponentStatus === next) return;
        statusEl.dataset.opponentStatus = next;

        if (ready) {
            statusEl.classList.add('ready');
            statusEl.innerHTML = `<span>✓</span> <span data-i18n="mulligan.opponentReady">Opponent is ready!</span>`;
        } else {
            statusEl.classList.remove('ready');
            statusEl.innerHTML = `<span class="spinner"></span> <span data-i18n="mulligan.opponentDeciding">Opponent is deciding...</span>`;
        }

        // Re-apply translations
        if (i18n && typeof i18n.translatePage === 'function') {
            i18n.translatePage();
        }
    }

    /**
     * Start the mulligan timer countdown
     */
    function startTimer() {
        stopTimer();

        const timerEl = document.getElementById('mulligan-timer');
        if (!timerEl) return;
        timerEl.hidden = untimed;
        if (untimed) return;

        /* Write into the VALUE span, never the pill. The countdown used to be
           a bare number and `timerEl.textContent = secondsLeft` was fine; it
           now owns a depleting bar as a child, and assigning textContent to the
           parent deletes every child node — the bar would vanish on the first
           tick and never come back. */
        const valueEl = timerEl.querySelector('.mulligan-timer-value');

        const updateTimer = () => {
            const now = Date.now();
            const deadline = Number.isFinite(localDeadlineAt)
                ? localDeadlineAt
                : now + mulliganTimeoutMs;
            const secondsLeft = Math.max(0, Math.ceil((deadline - now) / 1000));
            /* `textContent` is typed `string`; secondsLeft stays a NUMBER because
               the urgency thresholds below compare it. Assigning the number
               directly is what the old one-liner did and it is a real type
               error, so it is coerced here rather than at the source. */
            const digits = String(secondsLeft);
            if (valueEl) valueEl.textContent = digits;
            else timerEl.textContent = digits;

            /* Same contract as --turn-timer-progress: 1 at a full budget, 0 at
               zero. Guarded because a non-finite budget would emit
               `calc(NaN * 100%)`, which drops the declaration and leaves the
               bar full for the whole countdown. */
            const total = Number(mulliganTimeoutMs);
            if (Number.isFinite(total) && total > 0) {
                const progress = Math.min(1, Math.max(0, (deadline - now) / total));
                timerEl.style.setProperty('--mulligan-timer-progress', String(progress));
            }

            // Add warning classes
            timerEl.classList.remove('warning', 'critical');
            if (secondsLeft <= 5) {
                timerEl.classList.add('critical');
            } else if (secondsLeft <= 10) {
                timerEl.classList.add('warning');
            }

            if (deadline <= now) {
                stopTimer();
                console.log('[Mulligan] Authoritative deadline reached; waiting for server state');
                hide({ immediate: true, keepHandHidden: true });
                showWaitingBannerIfOpponentDeciding();
            }
        };

        updateTimer();
        timerInterval = setInterval(updateTimer, 1000);
    }

    /**
     * Stop the mulligan timer
     */
    function stopTimer() {
        if (timerInterval) {
            clearInterval(timerInterval);
            timerInterval = null;
        }
    }

    /**
     * Check if currently in mulligan phase.
     *
     * The dissolve is not the phase. hide() keeps `.active` on the overlay
     * for MULLIGAN_LEAVE_MS while it fades (see hide()), and socket-handler
     * refuses every card play while this returns true — so counting the fade
     * used to lose any click landing within ~400ms of the mulligan closing,
     * silently. show() strips `mulligan-leaving` on a re-show, so an overlay
     * that is both active and leaving is always one on its way out.
     * @returns {boolean}
     */
    function isActive() {
        if (pendingReveal) return true;
        return !!(overlayElement
            && overlayElement.classList.contains('active')
            && !overlayElement.classList.contains('mulligan-leaving'));
    }

    // Public API
    return {
        init,
        show,
        hide,
        holdPlayerHand,
        releasePlayerHand,
        hideWaitingBanner,
        updateOpponentStatus,
        isOpponentReady,
        isActive
    };
})();

export { Mulligan };
